import React, { useState } from 'react';
import {
  Box, Container, Typography, Tabs, Tab, Grid, Card, CardContent,
  Avatar, Chip, Button, Divider, Alert, AlertTitle,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Tooltip, Stack, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  TextField, CircularProgress
} from '@mui/material';
import {
  School as SchoolIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as AttendanceIcon,
  AccountBalanceWallet as FinanceIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  Logout as LogoutIcon,
  Language as LanguageIcon,
  AccessTime as TimeIcon,
  LocationOn as RoomIcon,
  SwapHoriz as SwitchAcademyIcon,
  FamilyRestroom as FamilyIcon,
  EventBusy as CancelledIcon,
  Schedule as PostponedIcon,
  PersonOff as AbsentTeacherIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import {
  Student, AcademySession, AcademyAttendance, PortalAcademy
} from '../../types/academy';
import { useStudentPortalLanguage, PortalLanguage } from './contexts/StudentPortalLanguageContext';
import { academyApi } from '../../services/academyApi';

interface Props {
  token?: string;
  onRefresh?: () => void;
  student?: Student | null;
  childrenList?: any[];
  academy: PortalAcademy;
  academiesCount: number;
  sessions: AcademySession[];
  attendances: AcademyAttendance[];
  invoices?: any[];
  payments: any[];
  disruptions: any[];
  onSwitchAcademy: () => void;
  onLogout: () => void;
}

export const StudentPortalDashboard: React.FC<Props> = ({
  token,
  onRefresh,
  student = null,
  childrenList = [],
  academy,
  academiesCount,
  sessions,
  attendances,
  invoices = [],
  payments = [],
  disruptions = [],
  onSwitchAcademy,
  onLogout,
}) => {
  const { t, language, setLanguage } = useStudentPortalLanguage();

  // Child tab (for parents with multiple children)
  const isParent = (student?.category === 'junior') || (childrenList && childrenList.length > 0);
  const [selectedChildIndex, setSelectedChildIndex] = useState(0);

  // Absence Justification Dialog State
  const [justifyingAttendance, setJustifyingAttendance] = useState<any | null>(null);
  const [justifReason, setJustifReason] = useState<string>('');
  const [justifDocName, setJustifDocName] = useState<string>('');
  const [justifDocBase64, setJustifDocBase64] = useState<string>('');
  const [submittingJustif, setSubmittingJustif] = useState<boolean>(false);
  const [justifError, setJustifError] = useState<string | null>(null);
  const [justifSuccess, setJustifSuccess] = useState<string | null>(null);

  const handleOpenJustifyModal = (att: any) => {
    setJustifyingAttendance(att);
    setJustifReason('');
    setJustifDocName('');
    setJustifDocBase64('');
    setJustifError(null);
    setJustifSuccess(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setJustifDocName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      setJustifDocBase64(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitJustification = async () => {
    if (!token || !justifyingAttendance || !justifReason.trim()) return;
    setSubmittingJustif(true);
    setJustifError(null);
    try {
      const ssnId = justifyingAttendance.ssn_idf || justifyingAttendance.session_id || justifyingAttendance.SSN_IDF;
      const stdId = activeStudent.id || (activeStudent as any).STD_IDF;
      const res = await academyApi.portalStudentSaveAbsenceJustification(token, {
        session_id: Number(ssnId),
        student_id: stdId ? Number(stdId) : undefined,
        justification_reason: justifReason.trim(),
        doc_name: justifDocName,
        doc_base64: justifDocBase64
      });
      if (res && res.success) {
        setJustifSuccess(t('portal.student.justification_success'));
        setTimeout(() => {
          setJustifyingAttendance(null);
          setJustifSuccess(null);
          setJustifReason('');
          setJustifDocName('');
          setJustifDocBase64('');
          if (onRefresh) onRefresh();
        }, 1200);
      } else {
        setJustifError(res?.message || t('portal.student.justification_error'));
      }
    } catch (err: any) {
      setJustifError(err?.message || t('portal.student.justification_error'));
    } finally {
      setSubmittingJustif(false);
    }
  };

  const activeChild: any = isParent && childrenList && childrenList.length > 0 ? childrenList[selectedChildIndex] : null;
  const activeStudent: Student = (activeChild?.profile || activeChild?.student || activeChild || student || {}) as Student;

  const effectiveSessions: AcademySession[] = activeChild?.sessions || sessions || [];
  const effectiveAttendances: AcademyAttendance[] = Array.isArray(activeChild?.attendances) ? activeChild.attendances : (Array.isArray(attendances) ? attendances : []);

  // Invoices & Payments resolution (guard against empty arrays blocking fallback)
  const childInvoices = Array.isArray(activeChild?.invoices) ? activeChild.invoices : [];
  const effectiveInvoices: any[] = childInvoices.length > 0 ? childInvoices : (Array.isArray(invoices) && invoices.length > 0 ? invoices : childInvoices);

  const childPayments = Array.isArray(activeChild?.payments) ? activeChild.payments : [];
  const effectivePayments: any[] = childPayments.length > 0 ? childPayments : (Array.isArray(payments) && payments.length > 0 ? payments : childPayments);

  // Financial records for table: priority to invoices, then payments
  const financialRecords: any[] = effectiveInvoices.length > 0 ? effectiveInvoices : effectivePayments;

  // Active child disruptions & notifications
  const childDisruptions = Array.isArray(activeChild?.notifications) && activeChild.notifications.length > 0
    ? activeChild.notifications
    : (Array.isArray(activeChild?.disruptions) && activeChild.disruptions.length > 0 ? activeChild.disruptions : []);
  const effectiveDisruptions: any[] = childDisruptions.length > 0 ? childDisruptions : (Array.isArray(disruptions) ? disruptions : []);

  // Navigation tab
  const [currentTab, setCurrentTab] = useState<'overview' | 'schedule' | 'attendance' | 'finances' | 'profile'>('overview');

  // Language menu anchor
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);

  // Financial calculations
  const totalInvoiced = effectiveInvoices.reduce((acc, inv) => acc + (Number(inv.amount) || Number(inv.INV_AMOUNT) || Number(inv.INV_TOTAL_AMOUNT) || 0), 0);
  const totalPaidFromInvoices = effectiveInvoices.reduce((acc, inv) => acc + (Number(inv.paid_amount) || Number(inv.INV_AMOUNT_PAID) || (String(inv.status || inv.INV_STATUS).toUpperCase() === 'PAID' ? Number(inv.amount || inv.INV_AMOUNT || 0) : 0)), 0);
  const totalPaidFromPayments = effectivePayments.reduce((acc, p) => acc + (Number(p.amount) || Number(p.PMT_AMOUNT) || 0), 0);
  const totalPaid = Math.max(totalPaidFromInvoices, totalPaidFromPayments);
  const balanceRemaining = Math.max(0, totalInvoiced - totalPaid);

  // Attendance metrics
  const totalAttendances = effectiveAttendances.length;
  const presentCount = effectiveAttendances.filter(a => String(a.status).toUpperCase() === 'PRESENT').length;
  const absentCount = effectiveAttendances.filter(a => String(a.status).toUpperCase() === 'ABSENT').length;
  const lateCount = effectiveAttendances.filter(a => String(a.status).toUpperCase() === 'LATE').length;
  const attendanceRate = totalAttendances > 0 ? Math.round((presentCount / totalAttendances) * 100) : 100;

  // Upcoming sessions
  const upcomingSessions = effectiveSessions.filter(s => s.status !== 'COMPLETED' && s.status !== 'CANCELLED');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: (theme) => theme.palette.mode === 'dark' ? '#0f172a' : '#f8fafc' }}>
      {/* Top Navbar */}
      <Box
        sx={{
          bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1e293b' : '#ffffff',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 1.5,
          px: { xs: 2, md: 4 },
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={academy.logo_base64 || (academy as any).logo}
              variant="rounded"
              sx={{
                bgcolor: (academy.logo_base64 || (academy as any).logo) ? 'transparent' : 'info.main',
                background: (academy.logo_base64 || (academy as any).logo) ? 'transparent' : 'linear-gradient(135deg, #06b6d4, #0284c7)',
                width: 44,
                height: 44,
                borderRadius: 2,
                boxShadow: (academy.logo_base64 || (academy as any).logo) ? 'none' : '0 4px 10px rgba(6, 182, 212, 0.3)',
                border: (academy.logo_base64 || (academy as any).logo) ? '1px solid' : 'none',
                borderColor: 'divider',
                p: (academy.logo_base64 || (academy as any).logo) ? 0.5 : 0,
                '& img': {
                  objectFit: 'contain',
                  width: '100%',
                  height: '100%'
                }
              }}
            >
              <SchoolIcon />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={800} lineHeight={1.2}>
                {academy.dos_lib || academy.dos_code || t('portal.student.brand')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.2 }}>
                <Chip
                  label={isParent ? t('portal.student.parent_badge') : t('portal.student.adult_badge')}
                  size="small"
                  color={isParent ? 'secondary' : 'info'}
                  sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                />
                {academiesCount > 1 && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<SwitchAcademyIcon sx={{ fontSize: 14 }} />}
                    onClick={onSwitchAcademy}
                    sx={{ textTransform: 'none', fontSize: '0.72rem', py: 0, px: 0.5 }}
                  >
                    {t('portal.student.change_academy')}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          {/* User Controls & Language */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Tooltip title={t('portal.student.change_language')}>
              <IconButton onClick={(e) => setLangAnchor(e.currentTarget)} size="small">
                <LanguageIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={langAnchor}
              open={Boolean(langAnchor)}
              onClose={() => setLangAnchor(null)}
            >
              <MenuItem onClick={() => { setLanguage('fr'); setLangAnchor(null); }}>Français</MenuItem>
              <MenuItem onClick={() => { setLanguage('en'); setLangAnchor(null); }}>English</MenuItem>
              <MenuItem onClick={() => { setLanguage('ar'); setLangAnchor(null); }}>العربية</MenuItem>
            </Menu>

            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
              <Avatar
                src={activeStudent.photo_base64}
                sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.9rem' }}
              >
                {activeStudent.first_name?.[0] || 'E'}
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  {activeStudent.first_name} {activeStudent.last_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {activeStudent.code || `ID: ${activeStudent.id}`}
                </Typography>
              </Box>
            </Box>

            <Tooltip title={t('portal.student.logout')}>
              <IconButton onClick={onLogout} color="error" size="small">
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Parent Multi-Child Switcher */}
        {isParent && childrenList.length > 1 && (
          <Paper sx={{ mb: 3, borderRadius: 3, p: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Tabs
              value={selectedChildIndex}
              onChange={(_e, val) => setSelectedChildIndex(val)}
              variant="scrollable"
              scrollButtons="auto"
              textColor="secondary"
              indicatorColor="secondary"
            >
              {childrenList.map((child: any, idx) => {
                const cProf = child?.profile || child?.student || child || {};
                const cName = cProf.STD_FULL_NAME || `${cProf.first_name || cProf.STD_FIRST_NAME || ''} ${cProf.last_name || cProf.STD_LAST_NAME || ''}`.trim() || cProf.full_name || t('portal.student.child_fallback', idx + 1);
                return (
                  <Tab
                    key={child?.id || cProf?.id || cProf?.STD_IDF || idx}
                    icon={<FamilyIcon />}
                    iconPosition="start"
                    label={t('portal.student.child_tab_label', cName)}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  />
                );
              })}
            </Tabs>
          </Paper>
        )}

        {/* Financial Hold Warning */}
        {activeStudent.financial_hold && (
          <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 3, borderRadius: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>{t('portal.student.financial_hold_title')}</AlertTitle>
            {t('portal.student.financial_hold_warning')}
          </Alert>
        )}

        {/* Important Disruptions Alert Banner (Cancelled, Postponed, Teacher Absent) */}
        {effectiveDisruptions && effectiveDisruptions.length > 0 && (
          <Box sx={{ mb: 3 }}>
            {effectiveDisruptions.map((d: any, idx: number) => {
              let msg = '';
              let icon = <WarningIcon />;
              let sev: 'error' | 'warning' | 'info' = 'warning';

              const dType = String(d.type || d.status || '').toUpperCase();
              const isCancelled = dType === 'CANCELLED';
              const isPostponed = dType === 'POSTPONED';
              const isTeacherAbsent = dType === 'TEACHERABSENT' || dType === 'TEACHER_ABSENT' || Boolean(d.teacher_absent);

              const dateStr = d.date || '';
              const startTimeStr = d.start_time || '';
              const endTimeStr = d.end_time || '';
              const groupNameStr = d.group_name || d.groupName || '';
              const teacherNameStr = d.teacher_name || d.teacherName || '';
              const reasonStr = d.reason || d.status_reason || '';

              if (isCancelled) {
                msg = t('portal.student.disruption_cancelled', dateStr, startTimeStr, endTimeStr, reasonStr || t('portal.student.default_reason_admin'));
                icon = <CancelledIcon />;
                sev = 'error';
              } else if (isPostponed) {
                msg = t('portal.student.disruption_postponed', dateStr, d.postponed_date || d.postponedDate || t('portal.student.soon'), d.postponed_time || d.postponedTime || '—', reasonStr || t('portal.student.default_reason_pedagogic'));
                icon = <PostponedIcon />;
                sev = 'warning';
              } else if (isTeacherAbsent) {
                msg = t('portal.student.disruption_teacher_absent', dateStr, startTimeStr, endTimeStr);
                if (teacherNameStr) {
                  msg += ` (${teacherNameStr})`;
                }
                if (reasonStr) {
                  msg += ` - ${reasonStr}`;
                }
                icon = <AbsentTeacherIcon />;
                sev = 'info';
              }

              if (!msg) return null;

              return (
                <Alert key={d.id || d.sessionId || idx} severity={sev} icon={icon} sx={{ mb: 1, borderRadius: 2 }}>
                  <AlertTitle sx={{ fontWeight: 700 }}>
                    {groupNameStr ? `${t('portal.student.disruptions_alert_title')} • ${groupNameStr}` : t('portal.student.disruptions_alert_title')}
                  </AlertTitle>
                  {msg}
                </Alert>
              );
            })}
          </Box>
        )}

        {/* Dashboard Navigation Tabs */}
        <Paper sx={{ mb: 3, borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <Tabs
            value={currentTab}
            onChange={(_e, val) => setCurrentTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
            sx={{ px: 2, pt: 1 }}
          >
            <Tab value="overview" icon={<SchoolIcon />} iconPosition="start" label={t('portal.student.tab_overview')} sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab value="schedule" icon={<CalendarIcon />} iconPosition="start" label={t('portal.student.tab_schedule')} sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab value="attendance" icon={<AttendanceIcon />} iconPosition="start" label={t('portal.student.tab_attendance')} sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab value="finances" icon={<FinanceIcon />} iconPosition="start" label={t('portal.student.tab_finances')} sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab value="profile" icon={<PersonIcon />} iconPosition="start" label={t('portal.student.tab_profile')} sx={{ textTransform: 'none', fontWeight: 600 }} />
          </Tabs>
        </Paper>

        {/* TAB 1: OVERVIEW */}
        {currentTab === 'overview' && (
          <Box>
            {/* KPI Cards */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ borderRadius: 3, borderLeft: '4px solid #06b6d4', p: 1 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
                      {t('portal.student.attendance_rate')}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} color="info.main" sx={{ my: 0.5 }}>
                      {attendanceRate}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('portal.student.presences_out_of_sessions', presentCount, totalAttendances)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ borderRadius: 3, borderLeft: '4px solid #3b82f6', p: 1 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
                      {t('portal.student.upcoming_sessions_title')}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} color="primary.main" sx={{ my: 0.5 }}>
                      {upcomingSessions.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('portal.student.scheduled_sessions')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ borderRadius: 3, borderLeft: balanceRemaining > 0 ? '4px solid #ef4444' : '4px solid #10b981', p: 1 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
                      {t('portal.student.remaining_balance')}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} color={balanceRemaining > 0 ? 'error.main' : 'success.main'} sx={{ my: 0.5 }}>
                      {balanceRemaining.toLocaleString()} DZD
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('portal.student.paid_out_of_invoiced', totalPaid.toLocaleString(), totalInvoiced.toLocaleString())}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Next Sessions Preview */}
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              {t('portal.student.upcoming_sessions_title')}
            </Typography>
            {upcomingSessions.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                <Typography color="text.secondary">{t('portal.student.no_upcoming_sessions')}</Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {upcomingSessions.slice(0, 4).map(s => (
                  <Grid item xs={12} sm={6} key={s.id}>
                    <Card variant="outlined" sx={{ borderRadius: 3, p: 1, '&:hover': { boxShadow: 3 } }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {s.topic || s.group_name || t('portal.student.course_fallback')}
                          </Typography>
                          <Chip label={s.status} size="small" color="primary" variant="outlined" />
                        </Box>
                        <Stack spacing={0.8} sx={{ mt: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TimeIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {s.date} • {s.start_time} - {s.end_time}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RoomIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {s.room_name || t('portal.student.assigned_room_fallback')} • {t('portal.student.teacher_short_label')} : {s.teacher_name || t('portal.student.teacher_fallback')}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* TAB 2: SCHEDULE */}
        {currentTab === 'schedule' && (
          <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              {t('portal.student.tab_schedule')}
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{t('portal.student.date_label')}</strong></TableCell>
                    <TableCell><strong>{t('portal.student.time_label')}</strong></TableCell>
                    <TableCell><strong>{t('portal.student.group_label')}</strong></TableCell>
                    <TableCell><strong>{t('portal.student.room_label')}</strong></TableCell>
                    <TableCell><strong>{t('portal.student.teacher_label')}</strong></TableCell>
                    <TableCell><strong>{t('portal.student.status_label')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {effectiveSessions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary">{t('portal.student.no_upcoming_sessions')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    effectiveSessions.map((s, idx) => (
                      <TableRow key={s.id || idx} hover>
                        <TableCell>{s.date}</TableCell>
                        <TableCell>{s.start_time} - {s.end_time}</TableCell>
                        <TableCell><strong>{s.group_name || s.topic || '—'}</strong></TableCell>
                        <TableCell>{s.room_name || '—'}</TableCell>
                        <TableCell>{s.teacher_name || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={s.status}
                            size="small"
                            color={s.status === 'COMPLETED' ? 'success' : s.status === 'CANCELLED' ? 'error' : s.status === 'POSTPONED' ? 'warning' : 'primary'}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* TAB 3: ATTENDANCE */}
        {currentTab === 'attendance' && (
          <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                {t('portal.student.tab_attendance')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip label={`${t('portal.student.attendance_present')}: ${presentCount}`} color="success" size="small" />
                <Chip label={`${t('portal.student.attendance_absent')}: ${absentCount}`} color="error" size="small" />
                <Chip label={`${t('portal.student.attendance_late')}: ${lateCount}`} color="warning" size="small" />
              </Box>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{t('portal.student.session_id_col')}</strong></TableCell>
                    <TableCell><strong>{t('portal.student.attendance_status_col')}</strong></TableCell>
                    <TableCell><strong>{t('portal.student.checkin_time_col')}</strong></TableCell>
                    <TableCell><strong>{t('portal.student.method_col')}</strong></TableCell>
                    <TableCell><strong>{t('portal.student.justification_col')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {effectiveAttendances.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary">{t('portal.student.no_attendance_records')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    effectiveAttendances.map((a, idx) => (
                      <TableRow key={a.id || idx} hover>
                        <TableCell>{t('portal.student.session_item_prefix', a.ssn_idf || (a as any).session_id || idx + 1)}</TableCell>
                        <TableCell>
                          {(() => {
                            const st = String(a.status || '').toUpperCase();
                            const label = st === 'PRESENT' ? t('portal.student.attendance_present') : st === 'ABSENT' ? t('portal.student.attendance_absent') : st === 'LATE' ? t('portal.student.attendance_late') : t('portal.student.attendance_excused');
                            const color: 'success' | 'error' | 'warning' = st === 'PRESENT' ? 'success' : st === 'ABSENT' ? 'error' : 'warning';
                            return <Chip label={label} size="small" color={color} />;
                          })()}
                        </TableCell>
                        <TableCell>{a.arrival_time || (a as any).ATD_CHECK_IN_TIME || '—'}</TableCell>
                        <TableCell>
                          <Chip label={a.scan_type || (a as any).ATD_SCAN_TYPE || t('portal.student.manual_scan_type')} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          {a.justified || (a as any).ATD_JUSTIFIED ? (
                            <Chip label={`${t('portal.student.justified')} : ${a.justification_reason || (a as any).ATD_JUSTIFICATION_REASON || 'OK'}`} color="info" size="small" />
                          ) : String(a.status || '').toUpperCase() === 'ABSENT' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="error.main">{t('portal.student.unjustified')}</Typography>
                              {token && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  sx={{ textTransform: 'none', py: 0, px: 1, fontSize: '0.72rem' }}
                                  onClick={() => handleOpenJustifyModal(a)}
                                >
                                  {t('portal.student.btn_justify')}
                                </Button>
                              )}
                            </Box>
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* TAB 4: FINANCES */}
        {currentTab === 'finances' && (
          <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              {t('portal.student.tab_finances')}
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">{t('portal.student.total_invoiced')}</Typography>
                    <Typography variant="h5" fontWeight={700}>{totalInvoiced.toLocaleString()} DZD</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">{t('portal.student.total_paid')}</Typography>
                    <Typography variant="h5" fontWeight={700} color="success.main">{totalPaid.toLocaleString()} DZD</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">{t('portal.student.remaining_balance')}</Typography>
                    <Typography variant="h5" fontWeight={700} color={balanceRemaining > 0 ? 'error.main' : 'success.main'}>
                      {balanceRemaining.toLocaleString()} DZD
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              {t('portal.student.financial_history_title')}
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{t('portal.student.invoice_number_col')}</strong></TableCell>
                    <TableCell><strong>{t('portal.student.date_label')}</strong></TableCell>
                    <TableCell><strong>{t('portal.student.amount_col')}</strong></TableCell>
                    <TableCell><strong>{t('portal.student.status_label')}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {financialRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary">{t('portal.student.no_financial_records')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    financialRecords.map((p, idx) => (
                      <TableRow key={p.id || idx} hover>
                        <TableCell><strong>{p.number || p.INV_NUMBER || `FAC-${p.id || idx + 1}`}</strong></TableCell>
                        <TableCell>{p.date || p.INV_DATE || '—'}</TableCell>
                        <TableCell>{(Number(p.amount) || Number(p.INV_AMOUNT) || 0).toLocaleString()} DZD</TableCell>
                        <TableCell>
                          <Chip
                            label={String(p.status || p.INV_STATUS || '').toUpperCase() === 'PAID' ? t('portal.student.status_paid') : String(p.status || p.INV_STATUS || '').toUpperCase() === 'PARTIAL' ? t('portal.student.status_partial') : t('portal.student.status_unpaid')}
                            size="small"
                            color={String(p.status || p.INV_STATUS || '').toUpperCase() === 'OVERDUE' || String(p.status || p.INV_STATUS || '').toUpperCase() === 'UNPAID' ? 'error' : String(p.status || p.INV_STATUS || '').toUpperCase() === 'PARTIAL' ? 'warning' : 'success'}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* TAB 5: PROFILE */}
        {currentTab === 'profile' && (
          <Paper variant="outlined" sx={{ borderRadius: 3, p: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
              {t('portal.student.tab_profile')}
            </Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">{t('portal.student.info_birth')}</Typography>
                <Typography variant="body1" fontWeight={600}>{activeStudent.date_of_birth || '—'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">{t('portal.student.info_phone')}</Typography>
                <Typography variant="body1" fontWeight={600}>{activeStudent.phone || '—'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">{t('portal.student.info_email')}</Typography>
                <Typography variant="body1" fontWeight={600}>{activeStudent.email || '—'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">{t('portal.student.info_emergency')}</Typography>
                <Typography variant="body1" fontWeight={600}>{activeStudent.emergency_contact || '—'}</Typography>
              </Grid>
              {activeStudent.school_level && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">{t('portal.student.info_level')}</Typography>
                  <Typography variant="body1" fontWeight={600}>{activeStudent.school_level}</Typography>
                </Grid>
              )}
              {activeStudent.guardian_first_name && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">{t('portal.student.info_guardian')}</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {activeStudent.guardian_first_name} {activeStudent.guardian_last_name} ({activeStudent.guardian_relation || t('portal.student.parent_relation_fallback')})
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}
      </Container>

      {/* Absence Justification Dialog */}
      <Dialog
        open={Boolean(justifyingAttendance)}
        onClose={() => !submittingJustif && setJustifyingAttendance(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('portal.student.justify_absence_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('portal.student.justify_absence_desc')}
          </DialogContentText>
          {justifError && <Alert severity="error" sx={{ mb: 2 }}>{justifError}</Alert>}
          {justifSuccess && <Alert severity="success" sx={{ mb: 2 }}>{justifSuccess}</Alert>}
          <TextField
            autoFocus
            margin="dense"
            label={t('portal.student.justification_reason_label')}
            fullWidth
            multiline
            rows={3}
            value={justifReason}
            onChange={(e) => setJustifReason(e.target.value)}
            disabled={submittingJustif}
            sx={{ mb: 2 }}
          />
          <Box>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={submittingJustif}
              sx={{ textTransform: 'none' }}
            >
              {justifDocName ? justifDocName : t('portal.student.justification_doc_label')}
              <input
                type="file"
                hidden
                accept="image/*,application/pdf"
                onChange={handleFileChange}
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJustifyingAttendance(null)} disabled={submittingJustif}>
            {t('portal.student.cancel')}
          </Button>
          <Button
            onClick={handleSubmitJustification}
            variant="contained"
            color="primary"
            disabled={submittingJustif || !justifReason.trim()}
          >
            {submittingJustif ? <CircularProgress size={20} color="inherit" /> : t('portal.student.btn_submit_justification')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
